import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InternalReturn } from '../../models/inventory/internal-return';
import { InternalReturnSearchCommand } from '../../models/inventory/internal-return-search';
import { ApiResponse, EntityKeyByReference, PaginatedApiResponse } from '@card-software/library';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class InternalReturnProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/internal-return';

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<InternalReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<InternalReturn>>(`${this.baseUrl}/${id}`, { headers });
  }

  public search(
    searchCommand: InternalReturnSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
    skipInterceptor = false,
  ): Observable<ApiResponse<PaginatedApiResponse<InternalReturn>>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<InternalReturn>>>(this.baseUrl + '/search' + queryParams, searchCommand, {
      headers,
    });
  }

  public createFromEntityKey(entityKey: EntityKeyByReference, skipInterceptor = false): Observable<ApiResponse<InternalReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<InternalReturn>>(this.baseUrl + '/entity-key', entityKey, { headers });
  }

  public update(id: string, body: InternalReturn, skipInterceptor = false): Observable<ApiResponse<InternalReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.put<ApiResponse<InternalReturn>>(`${this.baseUrl}/${id}`, body, { headers });
  }

  public updateStatus(id: string, newStatus: number, skipInterceptor = false): Observable<ApiResponse<InternalReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.put<ApiResponse<InternalReturn>>(`${this.baseUrl}/status/${id}`, newStatus, { headers });
  }

  public downloadReport(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/report/${id}`, { responseType: 'blob' });
  }
}
